import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../Api';

const PILOT_LIMIT = 500;
const PILOT_PER_COLOR = 10;
const UNLIMITED_LIMIT = 100000;
const STORAGE_KEY = 'sku-color-audit-pilot-v1';
const POLL_MS = 3000;
const LIVE_RESULT_LIMIT = 500;
const RESULT_PAGE_SIZE = 100;
const safeString = (value) => value === null || value === undefined ? '' : String(value);
const manualResult = (manualStatus, fallback) => ({
  matched: 'match',
  unmatched: 'mismatch',
  uncertain: 'uncertain',
  failed: 'failed',
  problem: 'failed',
}[manualStatus] || fallback);

const mapAuditResult = (row) => {
  const manualStatus = safeString(row.manual_match_status);
  const analyzerResult = row.status === 'missing' ? 'failed' : row.status;
  return ({
  id: row.id,
  sku_master_id: row.sku_master_id,
  sku: safeString(row.sku),
  fsn: safeString(row.fsn),
  account_name: safeString(row.account_name),
  brand: safeString(row.brand),
  expected: [row.expected_color1, row.expected_color2].filter(Boolean).join(' / '),
  image_url: safeString(row.image_url),
  dominantColor: safeString(row.dominant_color),
  confidence: Number(row.confidence) || 0,
  result: manualResult(manualStatus, analyzerResult),
  status: row.status,
  manualStatus,
  manualNote: safeString(row.manual_match_note),
  reason: safeString(row.error_message),
  errorCode: safeString(row.error_code),
  source: 'server-local-analyzer',
  });
};

const loadSavedAudit = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved?.results ? saved : null;
  } catch {
    return null;
  }
};

const savedJobRequest = (serverJob) => {
  if (!serverJob?.filter_json) return {};
  try {
    const snapshot = typeof serverJob.filter_json === 'string'
      ? JSON.parse(serverJob.filter_json)
      : serverJob.filter_json;
    return snapshot && typeof snapshot === 'object' ? snapshot : {};
  } catch {
    return {};
  }
};

const downloadBlob = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export default function SkuColorAuditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state || {};
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [resultStatus, setResultStatus] = useState('all');
  const [resultSearch, setResultSearch] = useState('');
  const [resultSort, setResultSort] = useState('original');
  const [resultColumnFilters, setResultColumnFilters] = useState({});
  const [resultColumnSort, setResultColumnSort] = useState({ key: '', direction: 'asc' });
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const [selectedResultIds, setSelectedResultIds] = useState(() => new Set());
  const [manualDrafts, setManualDrafts] = useState({});
  const [manualSavingId, setManualSavingId] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [selectedConfirmOpen, setSelectedConfirmOpen] = useState(false);
  const [resultPage, setResultPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const lastProcessedRef = useRef(null);
  const lastResultStatusRef = useRef(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [jobId, setJobId] = useState(null);
  const [serverJob, setServerJob] = useState(null);
  const runRef = useRef(false);

  useEffect(() => {
    const saved = loadSavedAudit();
    if (!saved) return;
    setRows(saved.rows || []);
    setResults(saved.results || []);
    if (saved.serverJob) setServerJob(saved.serverJob);
    // Do not resume a job id from localStorage. The server's latest job is the
    // source of truth after a reload; otherwise an old tab can show counters
    // from one job and rows from another.
  }, []);

  useEffect(() => {
    if (jobId) return undefined;
    let cancelled = false;
    api.get('/api/sku-color-audits/latest')
      .then((response) => {
        if (cancelled) return;
        const latestJob = response.data?.job || null;
        if (!latestJob) return;
        const latestResults = (response.data?.rows || []).map(mapAuditResult);
        setJobId(latestJob.id);
        setServerJob(latestJob);
        setResults(latestResults);
        if (latestResults.length) setRows(latestResults);
      })
      .catch(() => {
        // A missing audit table or an expired session should not block the page.
      });
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    if (!results.length) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        savedAt: new Date().toISOString(),
        jobId,
        serverJob,
        rows,
        results,
      }));
    } catch {
      // A full localStorage should not interrupt a read-only audit.
    }
  }, [jobId, serverJob, rows, results]);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const jobResponse = await api.get(`/api/sku-color-audits/${jobId}`);
        if (cancelled) return;
        const nextJob = jobResponse.data?.job || null;
        setServerJob(nextJob);
        setLastLiveUpdate(new Date());
        setRunning(['queued', 'running'].includes(nextJob?.status));
        const nextProcessed = Number(nextJob?.processed_count) || 0;
        const terminal = ['completed', 'failed', 'paused', 'stopped'].includes(nextJob?.status);
        if (lastProcessedRef.current !== nextProcessed || lastResultStatusRef.current !== nextJob?.status) {
          const resultsResponse = await api.get(`/api/sku-color-audits/${jobId}/results`, {
            params: {
              limit: terminal ? UNLIMITED_LIMIT : LIVE_RESULT_LIMIT,
              ...(resultStatus !== 'all' ? { status: resultStatus } : {}),
            },
          });
          if (cancelled) return;
          setResults((resultsResponse.data?.rows || []).map(mapAuditResult));
          lastProcessedRef.current = nextProcessed;
          lastResultStatusRef.current = nextJob?.status;
        }
        if (nextJob?.status === 'completed' || nextJob?.status === 'failed') {
          runRef.current = false;
          setRunning(false);
          if (nextJob.status === 'failed') setError(nextJob.last_error || 'Audit job failed');
        }
      } catch (pollError) {
        if (!cancelled) setError(pollError?.response?.data?.error || pollError?.message || 'Unable to read audit progress');
      }
    };
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [jobId, resultStatus]);

  useEffect(() => {
    setResultPage(1);
    setPageInput('1');
  }, [resultSearch, resultSort, resultStatus, resultColumnFilters, resultColumnSort]);

  const startPilot = async () => {
    setConfirmOpen(false);
    setLoadingRows(true);
    setError('');
    try {
      const previousRequest = savedJobRequest(serverJob);
      const ids = routeState.ids?.length ? routeState.ids : (previousRequest.ids || []);
      const savedFilters = Object.keys(routeState.queryParams || {}).length
        ? routeState.queryParams
        : (previousRequest.filters || {});
      const requestedFilters = { ...savedFilters };
      const mode = routeState.mode || (previousRequest.pilot === false ? 'filtered' : 'pilot');
      const pilot = mode === 'pilot';
      const filters = mode === 'selected' ? {} : requestedFilters;
      const sortBy = routeState.sortModel?.[0]?.field || previousRequest.sort_by || 'id';
      const sortDir = routeState.sortModel?.[0]?.sort || previousRequest.sort_dir || 'desc';
      const seedRows = Array.isArray(routeState.seedRows) ? routeState.seedRows : [];
      let pilotRows = pilot ? seedRows.slice(0, PILOT_LIMIT) : [];
      if (pilot && !pilotRows.length) {
        const params = {
          page: 1,
          pageSize: PILOT_LIMIT,
          lite: 1,
          sortBy: 'id',
          sortDir: 'desc',
          ...filters,
        };
        const response = await api.get('/api/skus', { params });
        pilotRows = (response.data?.data || response.data?.rows || []).slice(0, PILOT_LIMIT);
      }
      if (!pilotRows.length && !ids.length && !Object.keys(filters).length) throw new Error('No SKU rows matched the current filters');
      setRows(pilotRows);
      const response = await api.post('/api/sku-color-audits', {
        ids,
        filters,
        pilot,
        limit: pilot ? PILOT_LIMIT : UNLIMITED_LIMIT,
        sample_per_color: pilot ? PILOT_PER_COLOR : undefined,
        batch_size: 100,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      const createdJobId = response.data?.job_id;
      if (!createdJobId) throw new Error('Audit job was not created');
      setJobId(createdJobId);
      setServerJob({ status: 'queued', total_count: response.data.total || pilotRows.length, processed_count: 0 });
      setResults([]);
      runRef.current = true;
      setRunning(true);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'Unable to load pilot rows');
    } finally {
      setLoadingRows(false);
    }
  };

  const startSubsetAudit = async (ids, label = 'selected') => {
    if (!ids.length) {
      setError('Select at least one result row to process');
      return;
    }
    setError('');
    setLoadingRows(true);
    try {
      const response = await api.post('/api/sku-color-audits', {
        ids,
        filters: {},
        pilot: false,
        limit: ids.length,
        batch_size: 100,
        sort_by: 'id',
        sort_dir: 'asc',
      });
      const createdJobId = response.data?.job_id;
      if (!createdJobId) throw new Error('Audit job was not created');
      setRows(results.filter(row => ids.includes(Number(row.sku_master_id))));
      setJobId(createdJobId);
      setServerJob({ status: 'queued', total_count: response.data.total || ids.length, processed_count: 0 });
      setResults([]);
      runRef.current = true;
      setRunning(true);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || `Unable to process ${label} rows`);
    } finally {
      setLoadingRows(false);
    }
  };

  const controlJob = async (action) => {
    if (!jobId) return;
    setError('');
    setLoadingRows(true);
    try {
      const response = await api.post(`/api/sku-color-audits/${jobId}/control`, { action });
      setServerJob(response.data?.job || serverJob);
      setRunning(action === 'resume');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || `Unable to ${action} audit job`);
    } finally {
      setLoadingRows(false);
    }
  };

  const counts = useMemo(() => results.reduce((acc, row) => {
    const key = row.result || 'failed';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, { all: results.length, match: 0, mismatch: 0, uncertain: 0, failed: 0 }), [results]);
  const persistedCounts = useMemo(() => {
    if (!serverJob || serverJob.processed_count === undefined) return counts;
    return {
      all: Number(serverJob.processed_count) || 0,
      match: Number(serverJob.match_count) || 0,
      mismatch: Number(serverJob.mismatch_count) || 0,
      uncertain: Number(serverJob.uncertain_count) || 0,
      failed: (Number(serverJob.failed_count) || 0) + (Number(serverJob.missing_count) || 0),
    };
  }, [counts, serverJob]);
  const displayCounts = ['completed', 'paused', 'stopped'].includes(serverJob?.status) ? counts : persistedCounts;

  const resultTotalAvailable = resultStatus === 'all'
    ? Number(serverJob?.total_count) || displayCounts.all
    : displayCounts[resultStatus] || 0;
  const resultPreviewCap = running ? LIVE_RESULT_LIMIT : UNLIMITED_LIMIT;

  const filteredResults = useMemo(() => {
    const query = resultSearch.trim().toLowerCase();
    const filtered = results.filter((row) => {
      if (resultStatus !== 'all' && row.result !== resultStatus) return false;
      if (!query) return true;
      return [row.sku, row.fsn, row.account_name, row.expected, row.dominantColor, row.reason]
        .some(value => safeString(value).toLowerCase().includes(query));
    }).filter((row) => Object.entries(resultColumnFilters).every(([key, value]) => {
      const filter = safeString(value).trim().toLowerCase();
      return !filter || safeString(row[key]).toLowerCase().includes(filter);
    }));
    const sorted = [...filtered];
    if (resultSort === 'sku-asc') sorted.sort((a, b) => a.sku.localeCompare(b.sku));
    if (resultSort === 'sku-desc') sorted.sort((a, b) => b.sku.localeCompare(a.sku));
    if (resultSort === 'confidence-high') sorted.sort((a, b) => b.confidence - a.confidence);
    if (resultSort === 'confidence-low') sorted.sort((a, b) => a.confidence - b.confidence);
    if (resultSort === 'status') sorted.sort((a, b) => a.result.localeCompare(b.result) || a.sku.localeCompare(b.sku));
    if (resultColumnSort.key) {
      const { key, direction } = resultColumnSort;
      sorted.sort((left, right) => {
        const leftValue = key === 'confidence' ? Number(left[key]) || 0 : safeString(left[key]).toLowerCase();
        const rightValue = key === 'confidence' ? Number(right[key]) || 0 : safeString(right[key]).toLowerCase();
        const comparison = typeof leftValue === 'number'
          ? leftValue - rightValue
          : leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });
        return direction === 'desc' ? -comparison : comparison;
      });
    }
    return sorted;
  }, [resultSearch, resultSort, resultStatus, results, resultColumnFilters, resultColumnSort]);

  const resultHeaderColumns = [
    { key: 'sku', label: 'SKU' },
    { key: 'fsn', label: 'FSN' },
    { key: 'account_name', label: 'A/C' },
    { key: 'expected', label: 'Expected' },
    { key: 'dominantColor', label: 'Detected' },
    { key: 'confidence', label: 'Confidence' },
    { key: 'result', label: 'Result' },
    { key: 'reason', label: 'Reason' },
  ];

  const toggleResultColumnSort = (key) => {
    setResultColumnSort(previous => ({
      key,
      direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const setResultColumnFilter = (key, value) => {
    setResultColumnFilters(previous => ({ ...previous, [key]: value }));
  };

  useEffect(() => {
    setManualDrafts(previous => {
      const next = { ...previous };
      results.forEach(row => {
        const id = Number(row.sku_master_id);
        if (Number.isSafeInteger(id) && !(id in next)) next[id] = row.manualStatus || '';
      });
      return next;
    });
  }, [results]);

  const saveManualMatch = async (row, statusOverride) => {
    const skuMasterId = Number(row.sku_master_id);
    if (!Number.isSafeInteger(skuMasterId)) return;
    const status = statusOverride === undefined ? (manualDrafts[skuMasterId] || '') : statusOverride;
    setManualSavingId(skuMasterId);
    setError('');
    try {
      await api.put('/api/sku-color-audits/manual-match', {
        sku_master_id: skuMasterId,
        status,
      });
      setResults(previous => previous.map(item => Number(item.sku_master_id) === skuMasterId
        ? { ...item, manualStatus: status }
        : item));
      setManualDrafts(previous => ({ ...previous, [skuMasterId]: status }));
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Unable to save manual color match');
    } finally {
      setManualSavingId(null);
    }
  };

  useEffect(() => {
    const available = new Set(results.map(row => Number(row.sku_master_id)));
    setSelectedResultIds(previous => new Set([...previous].filter(id => available.has(id))));
  }, [results]);

  const selectedVisibleIds = filteredResults
    .slice((resultPage - 1) * RESULT_PAGE_SIZE, resultPage * RESULT_PAGE_SIZE)
    .map(row => Number(row.sku_master_id))
    .filter(Number.isSafeInteger);
  const selectedCount = selectedResultIds.size;
  const allVisibleSelected = selectedVisibleIds.length > 0
    && selectedVisibleIds.every(id => selectedResultIds.has(id));

  const toggleResultSelection = (id) => {
    setSelectedResultIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleVisibleSelection = () => {
    setSelectedResultIds(previous => {
      const next = new Set(previous);
      if (allVisibleSelected) selectedVisibleIds.forEach(id => next.delete(id));
      else selectedVisibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedResultIds(new Set(filteredResults
      .map(row => Number(row.sku_master_id))
      .filter(Number.isSafeInteger)));
  };

  const visibleResults = filteredResults.slice(
    (resultPage - 1) * RESULT_PAGE_SIZE,
    resultPage * RESULT_PAGE_SIZE,
  );
  const resultPageCount = Math.max(1, Math.ceil(filteredResults.length / RESULT_PAGE_SIZE));

  const goToPage = () => {
    const requested = Number.parseInt(pageInput, 10);
    if (!Number.isFinite(requested)) return;
    const nextPage = Math.min(resultPageCount, Math.max(1, requested));
    setResultPage(nextPage);
    setPageInput(String(nextPage));
  };

  const processSelected = async () => {
    setSelectedConfirmOpen(false);
    await startSubsetAudit([...selectedResultIds], 'selected');
    setSelectedResultIds(new Set());
  };

  const saveSelectedManualMatches = async (forceStatus) => {
    const selectedRows = results.filter(row => selectedResultIds.has(Number(row.sku_master_id)));
    if (!selectedRows.length) return;
    const items = selectedRows.map(row => ({
      sku_master_id: Number(row.sku_master_id),
      status: forceStatus || manualDrafts[Number(row.sku_master_id)] || row.manualStatus || (row.result === 'match' ? 'matched' : ''),
    }));
    const missingCount = items.filter(item => !item.status).length;
    if (!forceStatus && missingCount) {
      setNotice('');
      setError(`${missingCount} selected row(s) have no Match status. Choose a status in each row, or use Mark selected matched.`);
      return;
    }
    setBulkSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post('/api/sku-color-audits/manual-match/bulk', { items });
      const statusById = new Map(items.map(item => [item.sku_master_id, item.status]));
      setResults(previous => previous.map(row => statusById.has(Number(row.sku_master_id))
        ? {
          ...row,
          manualStatus: statusById.get(Number(row.sku_master_id)),
          result: manualResult(statusById.get(Number(row.sku_master_id)), row.result),
        }
        : row));
      setManualDrafts(previous => {
        const next = { ...previous };
        statusById.forEach((status, id) => { next[id] = status; });
        return next;
      });
      setSelectedResultIds(new Set());
      const savedCount = Number(response.data?.saved) || items.length;
      setNotice(`Saved ${savedCount.toLocaleString()} selected color match status${savedCount === 1 ? '' : 'es'}.`);
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Unable to save selected manual color matches');
    } finally {
      setBulkSaving(false);
    }
  };

  const exportResults = async (format) => {
    if (!results.length || !jobId) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    try {
      const response = await api.get(`/api/sku-color-audits/${jobId}/report`, {
        params: { format },
        responseType: 'blob',
      });
      const mimeType = format === 'json' ? 'application/json' : 'text/csv;charset=utf-8';
      downloadBlob(response.data, `sku-color-audit-${stamp}.${format}`, mimeType);
    } catch (reportError) {
      setError(reportError?.response?.data?.error || reportError?.message || 'Unable to download the audit report');
    }
  };

  const progress = serverJob?.progress?.percent ?? (rows.length ? Math.round((results.length / rows.length) * 100) : 0);
  const progressTotal = serverJob?.total_count || serverJob?.progress?.total || rows.length || (running ? PILOT_LIMIT : 0);
  const progressProcessed = serverJob?.progress?.processed ?? serverJob?.processed_count ?? results.length;

  return (
    <Stack spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', mb: 1 }}>
            <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/sku-upload')}>
              SKU Upload
            </Button>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>SKU Color Audit</Typography>
            <Chip icon={<ShieldOutlinedIcon />} color="success" variant="outlined" label="READ-ONLY" />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={loadingRows ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />} onClick={() => setConfirmOpen(true)} disabled={running || loadingRows}>
              {routeState.mode === 'selected' ? 'Analyze selected' : routeState.mode === 'filtered' ? 'Analyze filtered' : 'Start colour pilot'}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setConfirmOpen(true)} disabled={running || loadingRows || !results.length}>
              Run again
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportResults('csv')} disabled={!results.length}>
              Full CSV report
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportResults('json')} disabled={!results.length}>
              Full JSON report
            </Button>
            {serverJob?.status === 'running' && <Button color="warning" variant="outlined" onClick={() => controlJob('pause')} disabled={loadingRows}>Pause</Button>}
            {serverJob?.status === 'queued' && <Button color="warning" variant="outlined" onClick={() => controlJob('pause')} disabled={loadingRows}>Pause</Button>}
            {serverJob?.status === 'paused' && <Button color="success" variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => controlJob('resume')} disabled={loadingRows}>Resume</Button>}
            {['queued', 'running', 'paused'].includes(serverJob?.status) && <Button color="error" variant="outlined" onClick={() => controlJob('stop')} disabled={loadingRows}>Stop</Button>}
          </Stack>

          {(running || results.length > 0) && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2">Audit progress: {progressProcessed}/{progressTotal} processed</Typography>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" color="text.secondary">{progress}%</Typography>
                  {lastLiveUpdate && <Typography variant="caption" color="text.secondary">Live: {lastLiveUpdate.toLocaleTimeString()}</Typography>}
                </Stack>
              </Stack>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Results loaded: {results.length} of {Number(serverJob?.total_count) || results.length}. {running ? `Live preview is capped at ${LIVE_RESULT_LIMIT}; the completed report contains every result.` : 'Completed results are fully loaded.'}
              </Typography>
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {notice && <Alert severity="success" sx={{ mt: 2 }}>{notice}</Alert>}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="audit-status-filter-label">Result</InputLabel>
                <Select labelId="audit-status-filter-label" value={resultStatus} label="Result" onChange={event => setResultStatus(event.target.value)}>
                  <MenuItem value="all">All processed ({persistedCounts.all})</MenuItem>
                  <MenuItem value="match">Match ({persistedCounts.match})</MenuItem>
                  <MenuItem value="mismatch">Mismatch ({persistedCounts.mismatch})</MenuItem>
                  <MenuItem value="uncertain">Uncertain ({persistedCounts.uncertain})</MenuItem>
                  <MenuItem value="failed">Failed ({persistedCounts.failed})</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { key: 'all', label: 'All', count: displayCounts.all, color: 'default' },
                  { key: 'match', label: 'Match', count: displayCounts.match, color: 'success' },
                  { key: 'mismatch', label: 'Mismatch', count: displayCounts.mismatch, color: 'error' },
                  { key: 'uncertain', label: 'Uncertain', count: displayCounts.uncertain, color: 'warning' },
                  { key: 'failed', label: 'Failed', count: displayCounts.failed, color: 'default' },
                ].map(capsule => (
                  <Chip
                    key={capsule.key}
                    size="small"
                    clickable
                    color={capsule.color}
                    variant={resultStatus === capsule.key ? 'filled' : 'outlined'}
                    label={`${capsule.label} (${capsule.count})`}
                    onClick={() => setResultStatus(capsule.key)}
                    aria-label={`Show ${capsule.label} results`}
                  />
                ))}
              </Stack>
              <TextField size="small" label="Search results" value={resultSearch} onChange={event => setResultSearch(event.target.value)} sx={{ minWidth: 250 }} />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="audit-sort-label">Sort</InputLabel>
                <Select labelId="audit-sort-label" value={resultSort} label="Sort" onChange={event => setResultSort(event.target.value)}>
                  <MenuItem value="original">Original order</MenuItem>
                  <MenuItem value="sku-asc">SKU A-Z</MenuItem>
                  <MenuItem value="sku-desc">SKU Z-A</MenuItem>
                  <MenuItem value="confidence-high">Confidence high-low</MenuItem>
                  <MenuItem value="confidence-low">Confidence low-high</MenuItem>
                  <MenuItem value="status">Result status</MenuItem>
                </Select>
              </FormControl>
              <Chip size="small" variant="outlined" label={`${selectedCount} selected`} />
              <Button variant="outlined" size="small" startIcon={<PlaylistAddCheckIcon />} onClick={selectAllFiltered} disabled={running || loadingRows || bulkSaving || !filteredResults.length}>
                Select all filtered ({filteredResults.length})
              </Button>
              <Button variant="text" size="small" startIcon={<ClearAllIcon />} onClick={() => setSelectedResultIds(new Set())} disabled={running || loadingRows || bulkSaving || !selectedCount}>
                Clear selection
              </Button>
              <Button variant="contained" color="warning" size="small" onClick={() => setSelectedConfirmOpen(true)} disabled={running || loadingRows || !selectedCount}>
                Process selected
              </Button>
              <Button variant="outlined" size="small" startIcon={bulkSaving ? <CircularProgress size={14} /> : <SaveIcon />} onClick={() => saveSelectedManualMatches()} disabled={running || loadingRows || bulkSaving || !selectedCount}>
                {bulkSaving ? `Saving ${selectedCount.toLocaleString()}...` : 'Save selected'}
              </Button>
              <Button variant="outlined" color="success" size="small" startIcon={<CheckCircleOutlineIcon />} onClick={() => saveSelectedManualMatches('matched')} disabled={running || loadingRows || bulkSaving || !selectedCount}>
                Mark selected matched
              </Button>
            </Stack>
            <Stack alignItems="flex-end" spacing={0.25}>
              <Typography variant="body2" color="text.secondary">Server job with local analyzer</Typography>
              <Typography variant="caption" color="text.secondary">{results.length} loaded / {resultTotalAvailable} {resultStatus === 'all' ? 'total' : resultStatus}</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Filter, search, sort, and select result rows. Processing selected rows starts a separate read-only audit for exactly those rows.</Typography>
            <Typography variant="caption" color="text.secondary">Showing {filteredResults.length ? ((resultPage - 1) * RESULT_PAGE_SIZE) + 1 : 0}-{Math.min(resultPage * RESULT_PAGE_SIZE, filteredResults.length)} of {filteredResults.length} loaded{resultPreviewCap === LIVE_RESULT_LIMIT ? ` (live cap ${LIVE_RESULT_LIMIT})` : ''}</Typography>
          </Stack>
          <Box sx={{ overflowX: 'auto', mt: 1 }}>
            <Box component="table" sx={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', '& th, & td': { borderBottom: '1px solid', borderColor: 'divider', p: 1, textAlign: 'left', verticalAlign: 'middle' }, '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
              <thead>
                <tr>
                  <th><Checkbox size="small" checked={allVisibleSelected} indeterminate={selectedVisibleIds.some(id => selectedResultIds.has(id)) && !allVisibleSelected} onChange={toggleVisibleSelection} inputProps={{ 'aria-label': 'Select visible audit rows' }} /></th>
                  <th>Image</th>
                  {resultHeaderColumns.map(column => (
                    <th key={column.key} aria-sort={resultColumnSort.key === column.key ? `${resultColumnSort.direction}ending` : 'none'}>
                      <button
                        type="button"
                        onClick={() => toggleResultColumnSort(column.key)}
                        title={`Sort by ${column.label}`}
                        style={{ border: 0, background: 'transparent', padding: 0, font: 'inherit', fontWeight: 700, cursor: 'pointer' }}
                      >
                        {column.label}{resultColumnSort.key === column.key ? (resultColumnSort.direction === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                      </button>
                    </th>
                  ))}
                  <th>Match</th>
                </tr>
                <tr>
                  <th />
                  <th />
                  {resultHeaderColumns.map(column => (
                    <th key={column.key}>
                      <TextField
                        size="small"
                        fullWidth
                        value={resultColumnFilters[column.key] || ''}
                        onChange={event => setResultColumnFilter(column.key, event.target.value)}
                        placeholder={`Search ${column.label}`}
                        inputProps={{ 'aria-label': `Search ${column.label}` }}
                        sx={{ minWidth: column.key === 'reason' ? 150 : 110, '& .MuiInputBase-input': { py: 0.75, px: 1, fontSize: '0.75rem' } }}
                      />
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleResults.map(row => (
                  <tr key={`${row.id}-${row.sku}`}>
                    <td><Checkbox size="small" checked={selectedResultIds.has(Number(row.sku_master_id))} onChange={() => toggleResultSelection(Number(row.sku_master_id))} inputProps={{ 'aria-label': `Select ${row.sku}` }} /></td>
                    <td>{row.image_url ? <Box component="img" src={row.image_url} alt={row.sku} sx={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1 }} /> : <Typography variant="caption">No image</Typography>}</td>
                    <td>{row.sku || '-'}</td><td>{row.fsn || '-'}</td><td>{row.account_name || '-'}</td><td>{row.expected || '-'}</td><td>{row.dominantColor || '-'}</td>
                    <td>{Math.round((row.confidence || 0) * 100)}%</td>
                    <td><Chip size="small" color={row.result === 'match' ? 'success' : row.result === 'mismatch' ? 'error' : row.result === 'uncertain' ? 'warning' : 'default'} label={row.result || 'pending'} /></td>
                    <td>{row.reason || '-'}</td>
                    <td>
                      <Stack spacing={0.5} sx={{ minWidth: 150 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={manualDrafts[Number(row.sku_master_id)] ?? row.manualStatus ?? ''}
                            displayEmpty
                            onChange={event => setManualDrafts(previous => ({ ...previous, [Number(row.sku_master_id)]: event.target.value }))}
                            inputProps={{ 'aria-label': `Manual match status for ${row.sku}` }}
                          >
                            <MenuItem value=""><em>Not saved</em></MenuItem>
                            <MenuItem value="matched">Matched</MenuItem>
                            <MenuItem value="unmatched">Unmatched</MenuItem>
                            <MenuItem value="uncertain">Uncertain</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="problem">Problem</MenuItem>
                          </Select>
                        </FormControl>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={manualSavingId === Number(row.sku_master_id) ? <CircularProgress size={13} /> : <SaveIcon />}
                            onClick={() => saveManualMatch(row)}
                            disabled={manualSavingId !== null}
                          >
                            Save
                          </Button>
                          {row.result !== 'match' && manualDrafts[Number(row.sku_master_id)] !== 'matched' && (
                            <Button size="small" color="success" variant="outlined" onClick={() => saveManualMatch(row, 'matched')} disabled={manualSavingId !== null}>
                              Mark matched
                            </Button>
                          )}
                        </Stack>
                        {row.manualStatus && <Typography variant="caption" color="text.secondary">Saved: {row.manualStatus}</Typography>}
                      </Stack>
                    </td>
                  </tr>
                ))}
                {!visibleResults.length && <tr><td colSpan={11}><Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>No audit rows match the current result filter.</Typography></td></tr>}
              </tbody>
            </Box>
          </Box>
          {filteredResults.length > RESULT_PAGE_SIZE && (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="center" spacing={1.5} sx={{ mt: 2 }}>
              <Pagination count={resultPageCount} page={resultPage} onChange={(_, page) => { setResultPage(page); setPageInput(String(page)); }} color="primary" />
              <Stack direction="row" spacing={0.75} alignItems="center">
                <TextField
                  size="small"
                  type="number"
                  label="Page"
                  value={pageInput}
                  onChange={event => setPageInput(event.target.value)}
                  onKeyDown={event => { if (event.key === 'Enter') goToPage(); }}
                  inputProps={{ min: 1, max: resultPageCount, 'aria-label': 'Enter page number' }}
                  sx={{ width: 100 }}
                />
                <Button size="small" variant="outlined" onClick={goToPage}>Go</Button>
                <Typography variant="caption" color="text.secondary">of {resultPageCount}</Typography>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={selectedConfirmOpen} onClose={() => setSelectedConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process selected audit rows?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>The server will re-analyze exactly {selectedCount} selected SKU row(s).</Typography>
          <Typography variant="body2" color="text.secondary">This writes progress only to the separate audit tables. SKU master, image, lookup, and mapping data are never changed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={processSelected} startIcon={<PlayArrowIcon />}>Process selected</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{routeState.mode === 'selected' ? 'Analyze selected SKUs?' : routeState.mode === 'filtered' ? 'Analyze all filtered SKUs?' : 'Start read-only color pilot?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {routeState.mode === 'selected'
              ? 'Every selected SKU row will be read by the server-side local analyzer.'
              : routeState.mode === 'filtered'
                ? 'Every SKU row matching the current filters will be read by the server-side local analyzer.'
                : `Up to ${PILOT_PER_COLOR} SKU rows per COLOR value, capped at ${PILOT_LIMIT} rows, will be read by the server-side local analyzer.`}
          </Typography>
          <Typography variant="body2" color="text.secondary">Only the separate audit job/result tables are written for progress and resume. SKU, image, lookup, and mapping data are never changed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={startPilot} startIcon={<PlayArrowIcon />}>{routeState.mode === 'selected' ? 'Analyze selected' : routeState.mode === 'filtered' ? 'Analyze filtered' : 'Start pilot'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
